import React, { useMemo, useState } from 'react';
import { Add16Regular, Bookmark16Regular, Search16Regular } from '@fluentui/react-icons';
import {
    formatConversationTime,
    groupConversationsByRecency,
} from '../../utils/askAiChatMeta';
import styles from './askAi.module.css';

const SECTION_META = [
    { key: 'today', label: 'Today' },
    { key: 'previous7Days', label: 'Previous 7 Days' },
    { key: 'older', label: 'Older' },
];

const AskAiChatSidebar = ({
    conversations = [],
    loading = false,
    activeConversationId = null,
    onNewChat,
    onSelectChat,
    onOpenSavedPrompts,
}) => {
    const [search, setSearch] = useState('');

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return conversations;
        return conversations.filter(c => (c.title || 'Untitled chat').toLowerCase().includes(q));
    }, [conversations, search]);

    const groups = useMemo(() => groupConversationsByRecency(filtered), [filtered]);

    return (
        <aside className={styles.sidebar}>
            <div className={styles.sidebarTop}>
                <button type="button" className={styles.newChatBtn} onClick={onNewChat}>
                    <Add16Regular />
                    New chat
                </button>
                <div className={styles.searchBox}>
                    <Search16Regular className={styles.searchIcon} />
                    <input
                        type="search"
                        className={styles.searchInput}
                        placeholder="Search chats…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        aria-label="Search chats"
                    />
                </div>
            </div>

            <button type="button" className={styles.savedPromptsRow} onClick={onOpenSavedPrompts}>
                <Bookmark16Regular />
                Saved Prompts
            </button>

            <div className={styles.historyScroll}>
                {loading && !conversations.length ? (
                    <p className={styles.historyEmpty}>Loading chats…</p>
                ) : null}
                {!loading && !filtered.length ? (
                    <p className={styles.historyEmpty}>No chats yet. Start a new conversation.</p>
                ) : null}
                {SECTION_META.map(section => {
                    const items = groups[section.key] || [];
                    if (!items.length) return null;
                    return (
                        <div key={section.key} className={styles.historySection}>
                            <div className={styles.historySectionLabel}>{section.label}</div>
                            {items.map(conv => {
                                const active = conv.id === activeConversationId;
                                const exchanges = conv.exchange_count ?? 0;
                                const sources = conv.source_count ?? 0;
                                return (
                                    <button
                                        key={conv.id}
                                        type="button"
                                        className={`${styles.chatItem} ${active ? styles.chatItemActive : ''}`}
                                        onClick={() => onSelectChat(conv.id)}>
                                        <div className={styles.chatItemTitle}>
                                            {conv.title || 'Untitled chat'}
                                        </div>
                                        <div className={styles.chatItemMeta}>
                                            <span>
                                                {exchanges} exchange{exchanges === 1 ? '' : 's'} ·{' '}
                                                {sources} source{sources === 1 ? '' : 's'}
                                            </span>
                                            <span>{formatConversationTime(conv.updated_at)}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </aside>
    );
};

export default AskAiChatSidebar;
